'use client'

import { ArrowBtn } from '@/app/components/atoms/ArrowBtn'
import { TrackedProductCard } from '@/app/components/shared/TrackedProductCard'
import { useOptionalPageAnalytics } from '@/app/context/analytics'
import { buildProductCardClick } from '@/app/lib/analytics/buildCardClick'
import { withIndex } from '@/app/lib/analytics/mapProductToAnalyticsItem'
import type { EcommerceItemBase, ProductListAnalytics } from '@/app/lib/analytics/types'
import { useProductListTracking } from '@/app/lib/analytics/useProductListTracking'
import type { ProductCard } from '@/app/lib/api/mapProductToCard'
import { useCarouselProgress } from '@/app/lib/hooks/useCarouselProgress'
import { useDragScroll } from '@/app/lib/hooks/useDragScroll'
import { useRailScroll } from '@/app/lib/hooks/useRailScroll'
import { cx } from '@/app/lib/utils'

import { ProductPosterCard } from './ProductPosterCard'
import styles from './ProductCarousel.module.scss'

/**
 * Instrumentación opcional del carril (`view_item_list` por IntersectionObserver
 * + `select_item` + `click` de card). Sin ella no se emite nada (resto de
 * consumidores, p. ej. el módulo CMS «Productos»).
 */
export interface ProductCarouselAnalytics {
  readonly list: ProductListAnalytics
  /** Ítems base (sin `index`), mismo orden y longitud que `items`; si difiere no se instrumenta. */
  readonly items: readonly EcommerceItemBase[]
}

interface ProductCarouselProps {
  readonly items: readonly ProductCard[]
  readonly analytics?: ProductCarouselAnalytics
}

const NO_LIST: ProductListAnalytics = {
  itemListId: '',
  itemListName: '',
  moduleName: '',
  moduleType: '',
  modulePosition: '',
}

/**
 * Carril de tarjetas del módulo CMS «Productos»: carrusel horizontal en
 * cualquier tamaño de pantalla, con flechas de avanzar/retroceder superpuestas
 * SOLO desde escritorio (`useRailScroll`, compartido con `OtrosProductosIzq`/
 * `ProductosBotonCompra`) y una barra de progreso continua en cualquier
 * tamaño (`useCarouselProgress`, promovido a `src/app/lib/hooks/` — ver su
 * doc). En móvil/tablet
 * el desplazamiento es solo por gesto táctil, sin flechas (FR-009b).
 *
 * Un único item (o contenido que cabe entero sin desbordar) deja
 * `atStart`/`atEnd` en `true` y `hasOverflow` en `false` de forma natural —
 * ninguna flecha ni barra de progreso se muestran, sin necesidad de un caso
 * especial (Edge Cases de spec.md).
 *
 * `'use client'` por las flechas y por el cálculo de progreso: el resto del
 * módulo (`Productos.tsx`) es Server Component (Constitución, Principio I).
 */
export function ProductCarousel({ items, analytics: analyticsConfig }: ProductCarouselProps) {
  const pageAnalytics = useOptionalPageAnalytics()
  const hasAnalytics = analyticsConfig?.items.length === items.length && items.length > 0
  const { trackImpression, trackSelect } = useProductListTracking(
    analyticsConfig?.list ?? NO_LIST,
    hasAnalytics
      ? {
          trackViewItemList: pageAnalytics?.trackViewItemList,
          trackSelectItem: pageAnalytics?.trackSelectItem,
        }
      : {},
  )

  const { railRef, atStart, atEnd, recalc: recalcArrows, scrollByStep } = useRailScroll(items.length)
  const { hasOverflow, thumbWidth, thumbLeft, recalc: recalcProgress } = useCarouselProgress(
    railRef,
    items.length,
  )
  // Con ratón el carril no se arrastra de forma nativa (solo el gesto táctil).
  useDragScroll(railRef, items.length)

  const handleScroll = (): void => {
    recalcArrows()
    recalcProgress()
  }

  return (
    <div className={styles.container}>
      <div className={styles.viewport}>
        <div
          ref={railRef}
          className={styles.rail}
          data-fade-end={!atEnd}
          onScroll={handleScroll}
          data-testid="product-carousel"
        >
          {items.map(({ id, ...card }, index) => {
            const poster = <ProductPosterCard {...card} />
            const base = analyticsConfig?.items[index]
            if (!hasAnalytics || !base) {
              return (
                <div key={id} className={styles.item}>
                  {poster}
                </div>
              )
            }
            const item = withIndex(base, index)
            return (
              <div key={id} className={styles.item}>
                <TrackedProductCard
                  index={index}
                  onImpression={() => trackImpression(item.item_id, item)}
                  onSelect={(href) => {
                    trackSelect(item)
                    pageAnalytics?.trackClick(buildProductCardClick(item, href))
                  }}
                >
                  {poster}
                </TrackedProductCard>
              </div>
            )
          })}
        </div>

        <div className={styles.nav}>
          {!atStart && (
            <ArrowBtn
              dir="left"
              overlay
              className={cx(styles.navBtn, styles.navBtnLeft)}
              onClick={() => scrollByStep(-1)}
            />
          )}
          {!atEnd && (
            <ArrowBtn
              dir="right"
              overlay
              className={cx(styles.navBtn, styles.navBtnRight)}
              onClick={() => scrollByStep(1)}
            />
          )}
        </div>
      </div>

      {hasOverflow && (
        <div
          className={styles.progress}
          role="progressbar"
          aria-label="Progreso del carrusel de productos"
          aria-valuenow={Math.round(thumbLeft)}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div className={styles.track}>
            <div
              className={styles.thumb}
              style={{ width: `${thumbWidth}%`, left: `${thumbLeft}%` }}
            />
          </div>
        </div>
      )}
    </div>
  )
}
